/**
 * n8n Form section behaviour: multistep navigation, validation, submission.
 *
 * Every form instance initialises itself from the JSON config the section
 * renders. Nothing is addressed by a global id, so several forms can live on
 * one page and the theme editor can reload a single section.
 */
(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-n8n-form]';

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function controlsIn(element) {
    return toArray(element.querySelectorAll('[data-name]'));
  }

  // A free text field is named `<field>__text`, because a posted form carries no type and the
  // survey report needs the mark. Everything stored before the mark existed, a draft on this
  // device, a saved row, a link parameter, holds the bare name. Read both, new name first.
  var TEXT_SUFFIX = '__text';

  function baseName(name) {
    return name && name.slice(-TEXT_SUFFIX.length) === TEXT_SUFFIX
      ? name.slice(0, -TEXT_SUFFIX.length)
      : name;
  }

  function lookUp(source, name, get) {
    var found = get(source, name);
    if (found === undefined || found === null) {
      var bare = baseName(name);
      if (bare !== name) found = get(source, bare);
    }
    return found;
  }

  /**
   * Step containers in document order. A step is a theme block that holds its
   * own field blocks, so its position is its place in the DOM, not a counter.
   */
  function stepsIn(root) {
    return toArray(root.querySelectorAll('[data-n8n-step]'));
  }

  /**
   * A checkbox group cannot express "at least N" in HTML, so the count is
   * checked here and reported through the first box in the group.
   */
  function checkGroup(wrapper) {
    var boxes = toArray(wrapper.querySelectorAll('input[type="checkbox"]'));
    if (!boxes.length) return true;

    var checked = boxes.filter(function (box) {
      return box.checked;
    }).length;
    var min = parseInt(wrapper.getAttribute('data-min') || '0', 10);
    var max = parseInt(wrapper.getAttribute('data-max') || '0', 10);
    var message = '';

    if (min && checked < min) {
      message = wrapper.getAttribute('data-group-message') || 'Select at least ' + min + '.';
    } else if (max && checked > max) {
      message = wrapper.getAttribute('data-group-message') || 'Select at most ' + max + '.';
    }

    boxes[0].setCustomValidity(message);
    return message === '';
  }

  function checkControl(control) {
    control.setCustomValidity('');
    if (control.checkValidity()) return true;

    var message = control.getAttribute('data-error-message');
    if (message) control.setCustomValidity(message);
    return false;
  }

  /** Returns the first invalid control in a step, or null when the step passes. */
  function firstInvalidIn(step) {
    var invalid = null;

    toArray(step.querySelectorAll('[data-n8n-field]')).forEach(function (wrapper) {
      if (invalid) return;

      if (wrapper.hasAttribute('data-group')) {
        if (!checkGroup(wrapper)) invalid = wrapper.querySelector('input[type="checkbox"]');
        return;
      }

      controlsIn(wrapper).some(function (control) {
        if (checkControl(control)) return false;
        invalid = control;
        return true;
      });
    });

    return invalid;
  }

  /** Hidden fields take their value from the query string, as n8n's do. */
  var FROM_QUERY = /^\?([A-Za-z0-9_.-]+)$/;

  function applyQueryDefaults(root) {
    var query = new URLSearchParams(window.location.search);
    // Every reader of `k` reads the same clean value. A link whose parameters were joined with
    // `?` instead of `&` carries the tail inside `k`, and a hidden field that took it raw posted
    // a key the workflow could not match. backerKey() is the one place that decides what `k` is.
    var key = backerKey();
    if (key) query.set('k', key); else query.delete('k');

    var fillable = toArray(root.querySelectorAll('input[data-name], textarea[data-name]')).filter(
      function (control) {
        // A tickbox's value is its option text, not something the owner typed.
        return control.type !== 'radio' && control.type !== 'checkbox';
      }
    );

    // First pass: a parameter named like the field fills it, as n8n's own form
    // does, and as a hidden field here always has. Hidden fields resolve here,
    // so the second pass can read them.
    fillable.forEach(function (control) {
      if (FROM_QUERY.test(control.value)) return;
      var byName = lookUp(query, control.getAttribute('data-name'), function (q, n) {
        return q.get(n);
      });
      if (byName !== null && byName !== undefined) control.value = byName;
    });

    // Second pass: a default value of "?name" takes the name parameter, or the
    // field called name when the URL carries no such parameter. That is how one
    // hidden field feeds several visible ones under different names.
    fillable.forEach(function (control) {
      var alias = FROM_QUERY.exec(control.value);
      if (!alias) return;

      var fromQuery = query.get(alias[1]);
      if (fromQuery === null) {
        var source = root.querySelector('[data-name="' + alias[1] + '"]');
        fromQuery = source && source !== control ? source.value : '';
      }
      // An absent value clears the field: the shopper must never see "?x".
      control.value = fromQuery || '';
    });
  }

  function serialize(root) {
    var data = new URLSearchParams();

    controlsIn(root).forEach(function (control) {
      var name = control.getAttribute('data-name');

      if (control.type === 'checkbox') {
        if (control.hasAttribute('data-boolean')) {
          data.append(name, control.checked ? 'true' : 'false');
        } else if (control.checked) {
          data.append(name, control.value);
        }
        return;
      }

      if (control.type === 'radio') {
        if (control.checked) data.append(name, control.value);
        return;
      }

      if (control.multiple) {
        toArray(control.selectedOptions).forEach(function (option) {
          data.append(name, option.value);
        });
        return;
      }

      data.append(name, control.value);
    });

    return data;
  }

  function serializeSteps(list) {
    var data = new URLSearchParams();

    list.forEach(function (step) {
      serialize(step).forEach(function (value, key) {
        data.append(key, value);
      });
    });

    return data;
  }

  /**
   * An n8n webhook answers 200 even when the workflow failed, and reports the
   * failure in an `error` key, or in an `errors` list when the node is set up
   * JSON:API style. The key itself is the failure: `{"error":""}` counts,
   * because an empty message is still n8n saying the run did not finish.
   *
   * A real error STATUS is the better signal, and `response.ok` above already
   * catches it. This covers the answer the survey workflow actually sends: 200,
   * with the failure in the body.
   *
   * Returns the message to log, or null when the answer is not a failure.
   */
  function errorIn(value) {
    var items = Array.isArray(value) ? value : [value];

    for (var index = 0; index < items.length; index += 1) {
      var item = items[index];
      if (!item || typeof item !== 'object') continue;

      if ('error' in item) {
        return String(item.error) || 'the webhook reported an error with no message';
      }
      // JSON:API puts the failures in a list. An empty list is not a failure.
      if ('errors' in item && (!Array.isArray(item.errors) || item.errors.length)) {
        return JSON.stringify(item.errors).slice(0, 200);
      }
    }

    return null;
  }

  // urlencoded keeps this a CORS "simple request": a JSON body would add an
  // OPTIONS preflight that the n8n webhook's Allowed Origins does not answer.
  function post(endpoint, body) {
    // no-referrer: this page's address carries the backer's `k`, and the Referer header would
    // otherwise hand it to every log between here and n8n.
    return fetch(endpoint, { method: 'POST', referrerPolicy: 'no-referrer', body: body })
      .then(function (response) {
      if (!response.ok) throw new Error('HTTP ' + response.status);

      // RFC 9457 says a problem document describes a failure. A 200 that carries
      // one is a failure whatever the body holds, and errorIn cannot see it:
      // there is no `error` key in that shape.
      var type = response.headers && response.headers.get ? response.headers.get('content-type') : '';
      if (type && type.indexOf('problem+json') !== -1) {
        throw new Error('the webhook sent a problem document');
      }

      // A 200 is not a success on its own. See errorIn.
      return response.text().then(function (text) {
        var value = null;
        try {
          value = JSON.parse(text);
        } catch (parseError) {
          // Not JSON. Only a bad status can fail a plain-text answer.
        }
        var reported = value === null ? null : errorIn(value);
        if (reported) throw new Error(reported);
        // Handed back so a step can show what the workflow answered. NEVER as
        // markup: see showResult.
        return value;
      });
    });
  }

  /**
   * The backer's key, taken from the link.
   *
   * The theme editor opens the page without a link, so there is no `k` and nothing to fill. A
   * key in the section settings stands in for one, in the EDITOR only: it is how the owner sees
   * a real row, their template rendered against it, and the names that row carries.
   */
  function backerKey(previewKey) {
    var key = new URLSearchParams(window.location.search).get('k');
    if (!key && window.Shopify && window.Shopify.designMode) key = String(previewKey || '').trim();
    // The shape, not merely the presence. `k` is HMAC-SHA256 hex, so it is 64 hex characters.
    //
    // Taken from the FRONT, and whatever follows is dropped. A newsletter that wrote
    // `?utm_source=` where it meant `&utm_source=` hands this `<key>?utm_source=newsletter`:
    // present, not a key, and it had the relay answering nothing while the backer watched an
    // empty form. That link is already in inboxes, so the key is read out of it rather than
    // refused. Anything with no 64-hex run at the front is still no key.
    var found = /^[0-9a-f]{64}(?![0-9a-f])/i.exec(key || '');
    return found ? found[0] : null;
  }

  /**
   * Fills the form from the backer row the relay holds, so no backer is asked
   * for a field the campaign already knows.
   *
   * `k` is the only thing sent. It is HMAC(backer_email, salt), so it names the
   * row without naming the backer, and the relay answers with a whitelist: no
   * salt, no customer gid, no discount code. A failure is silent on purpose —
   * an empty form the backer fills in by hand still works, so there is nothing
   * useful to say to them about it.
   *
   * Only an EMPTY field is filled. 95% of these backers are on a phone, and a
   * late answer must never overwrite what one of them has started typing.
   */
  function prefill(root, endpoint, previewKey) {
    var key = backerKey(previewKey);
    if (!endpoint || !key) return null;

    // POST, with `k` in the body. A GET would work — the relay forwards whatever method it is
    // given — but a GET puts `k` in a URL, and a URL is what caches key on and what access logs
    // record. A body is neither.
    //
    // no-referrer for the same reason: this page's own address carries `k`, and it would
    // otherwise be sent as the Referer on every call.
    return fetch(endpoint, {
      method: 'POST',
      referrerPolicy: 'no-referrer',
      body: new URLSearchParams({ k: key }).toString(),
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .then(function (row) {
        var reported = errorIn(row);
        if (reported) throw new Error(reported);

        var owned = [];

        toArray(root.querySelectorAll('input[data-name], textarea[data-name], select[data-name]'))
          .forEach(function (control) {
            if (control.type === 'radio' || control.type === 'checkbox') return;
            if (control.value) return;
            var value = lookUp(row, control.getAttribute('data-name'), function (r, n) {
              return r[n];
            });
            // The survey's own email wins over the Kickstarter one: a backer who
            // has answered before sees what they last told us, not the old value.
            if (value === undefined && control.getAttribute('data-name') === 'shopify_email') {
              value = row.shopify_email || row.email;
            }
            if (value !== undefined && value !== null) {
              control.value = value;
              owned.push(control.getAttribute('data-name'));
            }
          });
        // The campaign owns `owned`: those came from the data table, so a draft saved on this
        // device must never put a stale answer back over one of them. The row goes with it,
        // because a backer who has already answered is shown their answer, not the form.
        return { owned: owned, row: row };
      })
      .catch(function (error) {
        console.warn('[n8n-form] prefill failed', error);
      });
  }

  /**
   * Posts one step to its own endpoint. Returns null when there is nothing to
   * send, which keeps a form without step endpoints synchronous.
   */
  function postStep(step) {
    var endpoint = step.getAttribute('data-n8n-endpoint');
    if (!endpoint) return null;

    var body = serialize(step).toString();
    // Back then Next must not post the same answers twice.
    if (step._n8nSent === body) return null;

    return post(endpoint, body).then(function (answer) {
      step._n8nSent = body;
      return answer;
    });
  }

  /**
   * Shows what a workflow answered, as TEXT. Never as markup.
   *
   * This page holds the backer's `k` in its own URL, and the answer is built
   * from data-table cells that backers themselves type. innerHTML here would
   * let one backer's answer run as script on the next backer's survey, and the
   * relay cannot help: it strips secret KEYS out of JSON and can strip nothing
   * at all out of an HTML blob.
   *
   * The shape, all of it optional:
   *   { "message": "one line", "fields": [{ "label": "Shipping to", "value": "GB" }] }
   * Anything else in the answer is ignored, so a workflow can send more keys
   * without touching the theme.
   */
  /**
   * The value a `[path]` names, dotted and array-indexed: `cart.items.0.title`.
   * A missing value is an empty string, never the word "undefined".
   */
  function valueAt(scope, path) {
    var parts = String(path).split('.');
    var value = scope;
    for (var i = 0; i < parts.length; i += 1) {
      if (value === null || typeof value !== 'object') return '';
      value = value[parts[i]];
    }
    if (value === null || value === undefined || typeof value === 'object') return '';
    return String(value);
  }

  /**
   * An href the page may follow. A `javascript:` or a `data:` URL in a workflow
   * answer is script, and this is the one place a backer's answer becomes an
   * attribute rather than text. https and a site-relative path, nothing else.
   */
  function safeHref(value) {
    var href = String(value).trim();
    if (/^https:\/\//i.test(href)) return href;
    if (/^\/[^/\\]/.test(href)) return href;
    return '';
  }

  /**
   * Fills one line of a template into a DOM node. Everything is textContent,
   * except a `| link:` placeholder, which becomes an <a> with a checked href.
   *
   *   Your cart is ready: [cart_permalink | button: Open your cart]
   */
  function fillLine(node, line, scope) {
    // Square brackets, not {{ }}: Shopify refuses a setting whose value looks
    // like Liquid, and the section's own progress label already reads [current].
    // `| link:` is a plain link, `| button:` the same thing wearing the theme's own button class,
    // so a cart link reads as the thing to press rather than as a line of blue text.
    var pattern = /\[\s*([\w.]+)\s*(?:\|\s*(link|button)\s*:\s*([^\]]*?))?\s*\]/g;
    var at = 0;
    var match;
    while ((match = pattern.exec(line)) !== null) {
      if (match.index > at) node.appendChild(document.createTextNode(line.slice(at, match.index)));
      at = pattern.lastIndex;
      var value = valueAt(scope, match[1]);
      if (match[2] === undefined) {
        node.appendChild(document.createTextNode(value));
        continue;
      }
      var href = safeHref(value);
      if (!href) continue;
      var link = document.createElement('a');
      link.href = href;
      link.textContent = (match[3] || '').trim() || href;
      // `btn` is the theme's own button class, the one the form's Next and Send already use, so
      // this follows whatever the owner sets in the theme editor.
      link.className = match[2] === 'button' ? 'btn n8n-form__result-button' : '';
      node.appendChild(link);
    }
    if (at < line.length) node.appendChild(document.createTextNode(line.slice(at)));
  }

  /**
   * The template as LINES of the owner's markup.
   *
   * A newline is a line break, a <br> is a line break, and the end of a paragraph is a line
   * break. Shopify's rich text field stores a mixture of all three — plain newlines with inline
   * tags among them — and a marker only means anything when it is alone on its line.
   *
   * The inline markup survives untouched. Only the line boundaries are normalised.
   */
  // Marks a line that came from a <li>, so it goes back out as one. A control character, because
  // no owner types one and nothing else in the pipeline reads it.
  var LIST_ITEM = '\u0001';

  function linesOf(markup) {
    return String(markup)
      .replace(/\r/g, '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<li[^>]*>/gi, '\n' + LIST_ITEM)
      .replace(/<\/li\s*>/gi, '\n')
      .replace(/<\/?(ul|ol)[^>]*>/gi, '\n')
      .replace(/<\/p\s*>/gi, '\n')
      .replace(/<p[^>]*>/gi, '')
      .split('\n');
  }

  // A quote the owner typed reaches here as either quote character, as the curly pair a text
  // editor substitutes, or as an entity: the rich text field stores whatever the editor gave it.
  var QUOTED = /^[\s"'\u2018\u2019\u201c\u201d]+|[\s"'\u2018\u2019\u201c\u201d]+$/g;

  // A compare never turns on case or on stray spaces. An absent field compares as empty.
  function fold(value) {
    return value === undefined || value === null ? '' : String(value).trim().toLowerCase();
  }

  /**
   * A line that is nothing but `[#name]`, `[#name=="value"]` or `[/name]` opens or closes a
   * block. The closing marker names the field alone, whatever the opening one compares.
   */
  function markerOf(line) {
    var text = String(line).replace(/<[^>]*>/g, '').split(LIST_ITEM).join('')
      .replace(/&quot;/gi, '"').replace(/&#0?39;|&apos;/gi, "'").trim();
    var open = text.match(/^\[#\s*([\w.]+)\s*(?:(==|!=)([^\]]*))?\]$/);
    if (open) {
      return { kind: 'open', name: open[1], op: open[2] || null, compare: (open[3] || '').replace(QUOTED, '') };
    }
    var close = text.match(/^\[\/\s*([\w.]+)\s*\]$/);
    if (close) return { kind: 'close', name: close[1] };
    // `[else]` names no field: it belongs to whichever block it stands in.
    if (/^\[\s*else\s*\]$/.test(text)) return { kind: 'else' };
    return null;
  }

  /**
   * Fills every text node of one line, inside a paragraph of its own.
   *
   * The safety rule, said once: the TEMPLATE is a theme setting, written by the owner and cut to
   * Shopify's own allowlist, so it may be markup. The ANSWER is built from cells backers type,
   * so every value from it is written as TEXT and never as markup.
   */
  function fillInto(node, line, scope) {
    // A condition inside ONE line of a repeated block, resolved against that item. The pass over
    // the whole template ran against the row, which knows nothing of an item's own fields.
    if (line.indexOf('[#') !== -1) line = resolveBlocks(line, scope);
    // A line out of a <li> goes back into one, inside a list of its own. Consecutive items share
    // that list, so a repeated block reads as the list the owner drew.
    var item = line.indexOf(LIST_ITEM) === 0;
    var para = document.createElement(item ? 'li' : 'p');
    if (!item) para.className = 'n8n-form__result-message';
    para.innerHTML = item ? line.slice(LIST_ITEM.length) : line;

    var texts = [];
    (function walk(element) {
      toArray(element.childNodes).forEach(function (child) {
        if (child.nodeType === 3) texts.push(child);
        else if (child.nodeType === 1) walk(child);
      });
    })(para);
    texts.forEach(function (text) {
      var filled = document.createDocumentFragment();
      fillLine(filled, text.nodeValue, scope);
      text.parentNode.replaceChild(filled, text);
    });

    if (!para.firstChild) return;
    if (!item) { node.appendChild(para); return; }
    var list = node.lastChild;
    if (!list || list.nodeName !== 'UL') {
      list = document.createElement('ul');
      list.className = 'n8n-form__result-list';
      node.appendChild(list);
    }
    list.appendChild(para);
  }

  /**
   * The owner's lines with the blocks resolved. A block over a list repeats the lines between its
   * markers once per item; over a single value it is a condition, kept when there is a value and
   * dropped whole when there is not. A marker that compares is always a condition.
   *
   * A line holding only `[else]` splits the block: the lines above it when the block is kept, the
   * lines below it when it is not. An empty list takes the `[else]` lines too, because a cart
   * with nothing in it is a backer with nothing to repeat over.
   */
  function renderLines(node, lines, scope) {
    // An empty line the owner left BETWEEN two lines is a blank line they meant. One at either
    // end is not: a rich text field ends every paragraph with one, and a template that began
    // with a marker would otherwise open with a gap. Several in a row are one gap.
    var gap = false;
    for (var i = 0; i < lines.length; i += 1) {
      var mark = markerOf(lines[i]);
      if (!mark || mark.kind !== 'open') {
        if (!lines[i].replace(/<[^>]*>/g, '').split(LIST_ITEM).join('').trim()) {
          gap = true;
          continue;
        }
        // Never between two list items: the empty line there is how one <li> ends and the next
        // begins, and a paragraph in the middle would cut the list in two.
        if (gap && node.firstChild && lines[i].indexOf(LIST_ITEM) !== 0) {
          var space = document.createElement('p');
          space.className = 'n8n-form__result-space';
          node.appendChild(space);
        }
        gap = false;
        fillInto(node, lines[i], scope);
        continue;
      }
      // Gather to the closing marker. An unclosed one takes the rest, which shows the owner their
      // mistake rather than silently dropping the block.
      //
      // The depth count is what keeps a nested block's own `[else]` and its closing marker out of
      // this one: only a marker at depth 0 belongs to the block being gathered.
      var body = [];
      var other = null;
      var depth = 0;
      i += 1;
      for (; i < lines.length; i += 1) {
        var end = markerOf(lines[i]);
        if (end && end.kind === 'close') {
          if (depth === 0 && end.name === mark.name) break;
          if (depth > 0) depth -= 1;
        } else if (end && end.kind === 'open') {
          depth += 1;
        } else if (end && end.kind === 'else' && depth === 0) {
          other = [];
          continue;
        }
        (other || body).push(lines[i]);
      }
      var value = scope && typeof scope === 'object' ? scope[mark.name] : null;
      // `[#settlement=="dropped"]` compares instead of asking for a value, so a block can turn on
      // WHICH value a field holds. An absent field compares as empty, which is why `!=` keeps its
      // block for one: the row says nothing, and nothing is not "dropped".
      // `keep` says which half of the block runs: the lines above `[else]`, or the ones below it.
      // Without an `[else]` there is no second half and a dropped block renders nothing.
      var keep;
      if (mark.op) {
        var same = fold(value) === fold(mark.compare);
        keep = mark.op === '==' ? same : !same;
      } else if (Array.isArray(value)) {
        if (value.length) {
          value.forEach(function (item) {
            renderLines(node, body, item && typeof item === 'object' ? item : { value: item });
          });
          continue;
        }
        keep = false;
      } else {
        // Absent, null, empty, false and 0 all mean "no value". A count of 0 is nothing to show.
        keep = !(value === undefined || value === null || value === '' || value === false || value === 0);
      }
      if (keep) renderLines(node, body, value && typeof value === 'object' ? value : scope);
      else if (other) renderLines(node, other, scope);
    }
  }

  /**
   * Renders the owner's template against the answer.
   *
   * The whole point is that a new field in an answer needs a template edit in the theme editor,
   * not a new theme block and a theme push.
   */
  function fillTemplate(node, template, answer) {
    // The conditions go first, over the whole template as the owner wrote it, so a marker does
    // not have to stand alone on its line: `[#settlement=="dropped"]It was dropped.[/settlement]`
    // reads as one sentence and opens and closes inside it. A list is left for the line
    // renderer, which repeats it with each item's own values in scope.
    renderLines(node, linesOf(resolveBlocks(template, answer, true)), answer);
  }

  // The opening marker of a block, anywhere in the markup.
  var OPEN = /\[#\s*([\w.]+)\s*(?:(==|!=)([^\]]*))?\]/g;

  /**
   * The next block at or after `from`, as { at, end, name, op, compare, body }.
   *
   * The closing marker is found by COUNTING the markers of that same name, not by matching the
   * first one: `[#settlement=="dropped"]…[#settlement=="settled"]…[/settlement]…[/settlement]`
   * has to close the inner block first. A block that is never closed is stepped over, so the
   * owner sees their own mistake and the blocks after it still resolve.
   */
  function nextBlock(markup, from) {
    OPEN.lastIndex = from;
    var open;
    while ((open = OPEN.exec(markup))) {
      var bodyAt = open.index + open[0].length;
      // The name is letters, digits, dots and underscores, so it is safe in a pattern.
      var ofName = new RegExp('\\[#\\s*' + open[1] + '\\s*(?:(?:==|!=)[^\\]]*)?\\]|\\[\\/\\s*' + open[1] + '\\s*\\]', 'g');
      ofName.lastIndex = bodyAt;
      var depth = 0;
      var found;
      while ((found = ofName.exec(markup))) {
        if (found[0].indexOf('[#') === 0) {
          depth += 1;
        } else if (depth === 0) {
          return { at: open.index, end: found.index + found[0].length, name: open[1],
            op: open[2] || null, compare: open[3] || '', body: markup.slice(bodyAt, found.index) };
        } else {
          depth -= 1;
        }
      }
      OPEN.lastIndex = bodyAt;
    }
    return null;
  }
  // Every marker, to count depth across a body.
  var MARKERS = /\[#\s*[\w.]+\s*(?:(?:==|!=)[^\]]*)?\]|\[\/\s*[\w.]+\s*\]|\[\s*else\s*\]/g;

  /** One body at `[else]`, ignoring an `[else]` that belongs to a block nested inside it. */
  function splitElse(body) {
    var depth = 0;
    var found;
    MARKERS.lastIndex = 0;
    while ((found = MARKERS.exec(body))) {
      if (found[0].indexOf('[#') === 0) depth += 1;
      else if (found[0].indexOf('[/') === 0) depth -= 1;
      else if (depth === 0) {
        return [body.slice(0, found.index), body.slice(found.index + found[0].length)];
      }
    }
    return [body, null];
  }

  /**
   * The blocks of a Custom HTML block resolved, in the markup itself.
   *
   * An answer template is rebuilt as paragraphs, which a Custom HTML block must not be: the
   * owner's own markup has to come out as it went in. So a condition is cut out of the HTML
   * here and nothing else is touched. The placeholders are filled afterwards, in the text
   * nodes, which is what keeps a value TEXT and never markup.
   *
   * A list says only whether it holds anything: `[#cart_content]` keeps its block for a cart
   * with items and takes the `[else]` lines for an empty one. It does not repeat, because a
   * repeat needs each item's own values put in per copy, and the answer template does that.
   *
   * ponytail: a pair of markers in DIFFERENT elements cuts between them and the browser closes
   * what the cut left open. Keep both markers of a pair inside one parent element.
   */
  function resolveBlocks(markup, row, keepLists) {
    var out = String(markup);
    var from = 0;
    // An ordinary template holds a handful of blocks. The cap is there so a marker this loop
    // cannot consume can never spin the page.
    for (var guard = 0; guard < 200; guard += 1) {
      var block = nextBlock(out, from);
      if (!block) return out;
      var at = block.at;
      var value = row && typeof row === 'object' ? row[block.name] : null;
      // A list is repeated by the line renderer, which puts each item's own values in scope.
      // Cutting it here would resolve the item's names against the row instead.
      if (keepLists && Array.isArray(value)) {
        from = block.end;
        continue;
      }
      var halves = splitElse(block.body);
      var keep;
      if (block.op) {
        var same = fold(value) === fold(String(block.compare).replace(QUOTED, ''));
        keep = block.op === '==' ? same : !same;
      } else if (Array.isArray(value)) {
        keep = value.length > 0;
      } else {
        keep = !(value === undefined || value === null || value === '' || value === false || value === 0);
      }
      var body = keep ? halves[0] : halves[1] || '';
      out = out.slice(0, at) + body + out.slice(block.end);
      // From the cut, not past it: a block inside the half that was kept is resolved next.
      from = at;
    }
    return out;
  }

  /**
   * Fills the [placeholders] a Custom HTML block holds, from the row the relay returned.
   *
   * The owner's markup is already in the page; only its text nodes are rewritten, and a value
   * goes in as TEXT, the same rule the answer template follows. Run with no row as well, after a
   * prefill that failed or never answered: a backer must never be shown a raw [firstname]. With
   * no row every condition takes its `[else]`, which is the same answer: nothing is known.
   */
  function fillStatic(root, row) {
    // In the theme editor with no row, the owner has to SEE their [names] and markers where they
    // put them. Emptying them leaves "Hi ," and nothing to edit. A backer never lands here: a
    // live page has a row, or clears the placeholders so no raw [firstname] is ever shown.
    if (!row && window.Shopify && window.Shopify.designMode) return;
    toArray(root.querySelectorAll('[data-n8n-fill]')).forEach(function (holder) {
      if (holder.hasAttribute('data-n8n-filled')) return;
      holder.setAttribute('data-n8n-filled', '');
      // Only when there is a marker: an untouched innerHTML keeps whatever the page already
      // parsed, and most blocks hold no block at all.
      if (/\[#\s*[\w.]+/.test(holder.innerHTML)) {
        holder.innerHTML = resolveBlocks(holder.innerHTML, row);
      }
      var texts = [];
      (function walk(element) {
        toArray(element.childNodes).forEach(function (child) {
          if (child.nodeType === 3) texts.push(child);
          else if (child.nodeType === 1) walk(child);
        });
      })(holder);
      texts.forEach(function (text) {
        if (text.nodeValue.indexOf('[') === -1) return;
        var filled = document.createDocumentFragment();
        fillLine(filled, text.nodeValue, row || {});
        text.parentNode.replaceChild(filled, text);
      });
    });
  }

  function showResult(node, answer, template) {
    if (!node) return;
    node.textContent = '';
    node.hidden = true;
    if (!answer || typeof answer !== 'object') return;

    // The owner's template owns the whole answer when there is one. message and
    // fields stay the default, so a step with no template still shows something.
    if (template && String(template).trim()) {
      fillTemplate(node, template, answer);
      node.hidden = !node.firstChild;
      return;
    }

    var text = typeof answer.message === 'string' ? answer.message.trim() : '';
    if (text) {
      var line = document.createElement('p');
      line.className = 'n8n-form__result-message';
      line.textContent = text;
      node.appendChild(line);
    }

    var fields = Array.isArray(answer.fields) ? answer.fields : [];
    if (fields.length) {
      var list = document.createElement('dl');
      list.className = 'n8n-form__result-fields';
      fields.forEach(function (field) {
        if (!field || typeof field !== 'object') return;
        var term = document.createElement('dt');
        term.textContent = String(field.label == null ? '' : field.label);
        var value = document.createElement('dd');
        value.textContent = String(field.value == null ? '' : field.value);
        list.appendChild(term);
        list.appendChild(value);
      });
      if (list.children.length) node.appendChild(list);
    }

    node.hidden = !node.firstChild;
  }

  /**
   * The "other" box. An option written `Something else|other` renders a text box beside it, and
   * what the backer types becomes THAT OPTION'S value — so the group still posts one field and
   * serialize() needs to know nothing about any of this.
   *
   * The value is `other:` plus what was typed. The prefix is what keeps a report readable: every
   * free answer is one of a kind, so without it each would be its own row beside the counted
   * options. A report groups on the prefix and counts them as one.
   *
   * An empty box leaves the value at `other`, with no colon: a tick must never be lost because
   * nothing was typed. The box only exists while its option is picked, so an unticked one posts
   * nothing.
   */
  function wireOther(root) {
    toArray(root.querySelectorAll('[data-n8n-other]')).forEach(function (box) {
      // getElementById, not a selector: the id is built by the theme from the section and block
      // ids, and CSS.escape is missing in older mobile browsers.
      var doc = root.ownerDocument || document;
      var control = doc.getElementById(box.getAttribute('data-n8n-other'));
      if (!control) return;

      var sync = function () {
        box.hidden = !control.checked;
        if (!control.checked) return;
        var typed = box.value.trim();
        control.value = typed ? 'other:' + typed : 'other';
      };

      // A radio group: picking another option hides this box, so every member is watched.
      var group = control.type === 'radio' && control.name
        ? toArray(root.querySelectorAll('input[type="radio"]')).filter(function (member) {
            return member.name === control.name;
          })
        : [control];
      group.forEach(function (member) { member.addEventListener('change', sync); });
      box.addEventListener('input', sync);
      sync();
    });
  }

  /**
   * The backer's answers, kept on their own device so a reload does not lose them. A survey is
   * long and most of these backers are on a phone, where a tab is dropped by the system without
   * warning.
   *
   * Keyed by `k`, so two backers who use the same device do not read each other's draft. Nothing
   * is sent anywhere: this is localStorage, and the campaign already holds what was submitted.
   *
   * A field the DATA TABLE filled is never restored. The table is the campaign's own record, and
   * a draft typed before the owner corrected a row would otherwise put the stale value back.
   */
  function draftKey(root) {
    var key = backerKey();
    return 'n8n-form:' + (root.getAttribute('data-section-id') || 'form') + ':' + (key || 'anon');
  }

  function readDraft(root) {
    try {
      return JSON.parse(window.localStorage.getItem(draftKey(root)) || '{}');
    } catch (error) {
      // Private browsing, blocked storage, or something that is not JSON. A draft is a
      // convenience: losing it must never stop the form working.
      return {};
    }
  }

  function saveDraft(root) {
    var answers = {};
    controlsIn(root).forEach(function (control) {
      var name = control.getAttribute('data-name');
      if (!name || control.type === 'hidden') return;
      if (control.type === 'checkbox' || control.type === 'radio') {
        if (!control.checked) return;
        (answers[name] = answers[name] || []).push(control.value);
        return;
      }
      if (control.value !== '') answers[name] = control.value;
    });
    try {
      window.localStorage.setItem(draftKey(root), JSON.stringify(answers));
    } catch (error) {
      // A full or blocked store. Nothing to tell the backer: they lose a convenience, not data.
    }
  }

  /** Puts a draft back, skipping every field the data table owns and every field already set. */
  function restoreDraft(root, owned) {
    var answers = readDraft(root);
    var skip = owned || [];
    controlsIn(root).forEach(function (control) {
      var name = control.getAttribute('data-name');
      if (!name || control.type === 'hidden' || skip.indexOf(name) !== -1) return;
      var saved = lookUp(answers, name, function (a, n) {
        return a[n];
      });
      if (saved === undefined || saved === null) return;
      if (control.type === 'checkbox' || control.type === 'radio') {
        if (!Array.isArray(saved)) return;
        if (saved.indexOf(control.value) !== -1) {
          control.checked = true;
          return;
        }
        // The "other" option is saved as what the backer TYPED, `other:<text>`, while the option
        // here still carries its declared value, `other`. A plain match therefore never finds it,
        // and the box came back unticked with the typed text lost.
        if (control.value === 'other') {
          for (var pick = 0; pick < saved.length; pick += 1) {
            if (saved[pick] === 'other' || String(saved[pick]).indexOf('other:') === 0) {
              control.checked = true;
              control.value = saved[pick];
              return;
            }
          }
        }
        return;
      }
      // A value already there came from the link or the table, and both outrank a draft.
      if (control.value === '' && !Array.isArray(saved)) control.value = saved;
    });
    // An "other" box reads its value back from the option it belongs to.
    toArray(root.querySelectorAll('[data-n8n-other]')).forEach(function (box) {
      var doc = root.ownerDocument || document;
      var control = doc.getElementById(box.getAttribute('data-n8n-other'));
      if (!control || !control.checked) return;
      if (control.value.indexOf('other:') === 0) box.value = control.value.slice(6);
      box.hidden = false;
    });
  }

  function init(root) {
    if (root.hasAttribute('data-n8n-ready')) return;
    root.setAttribute('data-n8n-ready', '');

    var configNode = root.querySelector('[data-n8n-config]');
    if (!configNode) return;

    var config = JSON.parse(configNode.textContent);
    var form = root.querySelector('form');
    var message = root.querySelector('[data-n8n-message]');
    var progress = root.querySelector('[data-n8n-progress]');
    var backButton = root.querySelector('[data-n8n-back]');
    var nextButton = root.querySelector('[data-n8n-next]');
    var submitButton = root.querySelector('[data-n8n-submit]');
    var result = root.querySelector('[data-n8n-result]');
    var loading = root.querySelector('[data-n8n-loading]');
    var again = root.querySelector('[data-n8n-again]');
    var steps = stepsIn(root);
    var current = 0;
    var sending = false;

    if (!form || !steps.length) return;

    // A page opened without a `k` has no backer behind it. The relay cannot be asked for the
    // row, every [name] in the templates would render empty, and an answer that did get sent
    // could never be matched to anyone. So the form stays shut, whether or not this section
    // carries a prefill URL: a survey with no backer is not a form to fill in, it is a bad link.
    // Not in the editor: the owner has no link there, and a hidden form cannot be edited.
    if (!backerKey(config.previewKey) && !(window.Shopify && window.Shopify.designMode)) {
      form.hidden = true;
      if (progress) progress.hidden = true;
      // In the overlay, which is the box this form already uses to speak for itself, and which
      // would otherwise be the last thing a backer saw. textContent replaces the spinner and
      // its label in one go, so nothing is left spinning behind the warning.
      var warning = loading || message;
      if (warning) {
        warning.textContent = config.invalidLinkMessage;
        warning.hidden = false;
      }
      return;
    }

    function show(index) {
      current = Math.max(0, Math.min(index, steps.length - 1));

      steps.forEach(function (step, position) {
        step.hidden = position !== current;
      });

      var multistep = steps.length > 1;
      if (backButton) backButton.hidden = !multistep || current === 0;
      if (nextButton) nextButton.hidden = !multistep || current === steps.length - 1;
      if (submitButton) submitButton.hidden = multistep && current !== steps.length - 1;
      if (progress) {
        progress.hidden = !multistep;
        progress.textContent = config.progressLabel
          .replace('[current]', String(current + 1))
          .replace('[total]', String(steps.length));
      }
    }

    function move(delta) {
      // The answer belonged to the step that was posted. Leaving it on screen
      // would read as an answer to the step the backer is now looking at.
      if (delta < 0) showResult(result, null);
      show(current + delta);
      root.scrollIntoView({ block: 'nearest' });
    }

    // Disabling the buttons is not enough: Enter, or the mobile keyboard's Go,
    // fires submit on the form while a post is still in flight.
    function busy(state) {
      sending = state;
      if (nextButton) nextButton.disabled = state;
      if (submitButton) submitButton.disabled = state;
      message.textContent = state ? config.sendingMessage : '';
    }

    /**
     * The post-submit screen, shown on LOAD for a backer who has already answered. The row the
     * relay returns carries `survey_completed`, and with it the cart and the code, so the same
     * answer template renders it — one description of what a sent survey looks like, not two.
     *
     * `survey_locked` closes it for good: the button disappears and the form stays shut. That is
     * a field in the row, so the owner locks the survey in n8n and no theme push is needed.
     */
    function showAnswered(row) {
      if (!row || row.survey_completed !== true) return;
      form.hidden = true;
      if (progress) progress.hidden = true;
      showResult(result, row, steps[steps.length - 1].getAttribute('data-n8n-template'));
      if (again) again.hidden = row.survey_locked === true;
    }

    if (again) {
      again.addEventListener('click', function () {
        // Their own answers are still in the draft, so the form opens where they left it.
        again.hidden = true;
        showResult(result, null);
        form.hidden = false;
        // The submit left `sending` true and both buttons disabled. That never showed while the
        // form was hidden, and it would have greeted the backer with a dead Send on the way back.
        busy(false);
        // A reopened form is a new pass, so the same answers may go again. postStep dedupes on
        // the body it last sent, which is there to stop Back-then-Next posting twice. Left
        // standing here it swallows the whole resend: every step returns null, nothing is
        // posted, and the backer is shown the sent screen as if it had been.
        steps.forEach(function (step) { delete step._n8nSent; });
        show(0);
        root.scrollIntoView({ block: 'nearest' });
      });
    }

    function advance(delta) {
      if (sending) return;

      if (delta > 0) {
        var invalid = firstInvalidIn(steps[current]);
        if (invalid) {
          invalid.reportValidity();
          return;
        }

        // A step with its own endpoint is posted on the way out, so the next
        // step only opens once n8n has the answers.
        var pending = postStep(steps[current]);
        if (pending) {
          busy(true);
          pending
            .then(function (answer) {
              busy(false);
              var posted = steps[current];
              move(1);
              showResult(result, answer, posted.getAttribute('data-n8n-template'));
              showVars(root, answer);
            })
            .catch(function (error) {
              console.error('[n8n-form] step post failed', error);
              busy(false);
              message.textContent = config.errorMessage;
            });
          return;
        }
      }
      move(delta);
    }

    function submit() {
      if (sending) return;

      // Validate every step, not just the last one, and surface the failure
      // where it can actually be seen: reportValidity() is silent on a hidden
      // element.
      for (var position = 0; position < steps.length; position += 1) {
        var invalid = firstInvalidIn(steps[position]);
        if (invalid) {
          show(position);
          invalid.reportValidity();
          return;
        }
      }

      if (root.querySelector('[data-n8n-honeypot]').value) return; // bot

      // Each step with its own endpoint posts there — the last step has had no
      // Next to trigger it. Every other step goes to the section's endpoint.
      var jobs = [];
      var leftovers = [];

      steps.forEach(function (step) {
        if (!step.getAttribute('data-n8n-endpoint')) {
          leftovers.push(step);
          return;
        }
        var pending = postStep(step);
        if (pending) jobs.push(pending);
      });

      var body = serializeSteps(leftovers).toString();

      if (body && !config.endpoint) {
        console.error('[n8n-form] no section webhook URL for ' + leftovers.length + ' step(s) without one');
        message.textContent = config.errorMessage;
        return;
      }
      if (body) jobs.push(post(config.endpoint, body));

      busy(true);

      Promise.all(jobs)
        .then(function (answers) {
          // The last workflow to answer is the one that saw the whole form.
          var answer = answers.filter(Boolean).pop();
          showResult(result, answer, steps[steps.length - 1].getAttribute('data-n8n-template'));
          showVars(root, answer);
          // The draft is KEPT. The campaign holds the answers now, but the prefill returns only
          // the columns the data table has — not one custom_ answer — so clearing it would make
          // a backer who reopens their link see an empty form and think nothing was sent.
          // Reopening shows what they sent, and a changed answer upserts over the old one.
          message.textContent = config.successMessage;
          form.hidden = true;
          if (progress) progress.hidden = true;
          // Just sent, so the survey is open by definition: the same button offers another go.
          if (again) again.hidden = false;
        })
        .catch(function (error) {
          console.error('[n8n-form] submit failed', error);
          busy(false);
          message.textContent = config.errorMessage;
        });
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      // Enter, or the mobile keyboard's "Go", fires submit on any step.
      if (current < steps.length - 1) {
        advance(1);
        return;
      }
      submit();
    });

    if (nextButton) {
      nextButton.addEventListener('click', function () {
        advance(1);
      });
    }
    if (backButton) {
      backButton.addEventListener('click', function () {
        advance(-1);
      });
    }

    // A custom message sticks until it is cleared.
    form.addEventListener('input', function (event) {
      if (event.target.setCustomValidity) event.target.setCustomValidity('');
    });

    // The editor's block-select handler lives outside init and needs the same
    // button rules, so it reuses this instance's show().
    root._n8nShow = show;

    // Saved on every change, so a tab the phone drops loses nothing. Cheap: a few fields of
    // text, written to this device only.
    // Every keystroke, every tick, off the keystroke's own critical path.
    //
    // localStorage.setItem is SYNCHRONOUS: left inline it blocks the handler, and the browser
    // cannot paint the character the backer just typed until the write returns. A timeout of 0
    // hands the write to the next task, so the keystroke renders first and the draft lands a
    // millisecond later.
    //
    // One write per turn, not one per event: `input` and `change` both fire on a tickbox, and
    // the saved state is read from the DOM anyway, so the later write would be identical.
    //
    // What this costs: the very last keystroke before the tab dies, which the owner called an
    // acceptable loss. What it buys: a form that never stutters while being typed into.
    var pending = false;
    var keepDraft = function () {
      if (pending) return;
      pending = true;
      window.setTimeout(function () {
        pending = false;
        saveDraft(root);
      }, 0);
    };
    form.addEventListener('input', keepDraft);
    form.addEventListener('change', keepDraft);

    wireOther(root);
    applyQueryDefaults(root);
    // After the query defaults, so a value in the link still wins.
    var filling = prefill(root, config.prefillEndpoint, config.previewKey);
    show(0);

    // The overlay covers the fill, not the page load. A backer who watched a
    // field fill itself under their thumb was being shown the relay's work;
    // with no prefill to wait for there is nothing to cover, so it never shows.
    //
    // It is hidden in the markup and shown HERE, so a browser that never runs
    // this file shows the plain form rather than a spinner that never stops.
    // The draft goes back AFTER the table has answered, never before: a field the campaign owns
    // must win, and prefill only fills an EMPTY field, so a draft restored first would silently
    // beat the data table.
    //
    // On the SAME deadline as the overlay. A relay that answers slowly delays the draft; a relay
    // that never answers at all must not cancel it, which waiting on the promise alone would do.
    if (!filling) {
      restoreDraft(root, []);
      fillStatic(root, null);
    } else {
      var restored = false;
      var putDraftBack = function (answered) {
        if (restored) return;
        restored = true;
        restoreDraft(root, (answered && answered.owned) || []);
        // The static blocks are filled whether or not the relay answered: with no row every
        // placeholder resolves to empty, which beats showing a backer a raw [firstname].
        fillStatic(root, answered && answered.row);
        // A backer who has already answered is shown what they sent, not an empty form.
        if (answered && answered.row) showAnswered(answered.row);
        showVars(root, answered && answered.row);
      };
      var late = window.setTimeout(function () { putDraftBack(null); }, 8000);
      var done = function (answered) { window.clearTimeout(late); putDraftBack(answered); };
      filling.then(done, function () { done(null); });

      if (loading) {
        loading.hidden = false;
        root.setAttribute('data-n8n-filling', '');
        var reveal = function () {
          loading.hidden = true;
          root.removeAttribute('data-n8n-filling');
        };
        // A relay that never answers must not hold the form shut. The backer can
        // fill it in by hand, which is what the silent prefill failure assumes.
        var guard = window.setTimeout(reveal, 8000);
        filling.then(function () { window.clearTimeout(guard); reveal(); },
          function () { window.clearTimeout(guard); reveal(); });
      }
    }

    if (window.Shopify && window.Shopify.designMode) {
      warnAboutPatterns(root);
      // Always, even with no key and no row: the panel is where the owner is told to set one.
      // It was only ever built from an answer, so a form that never prefilled showed nothing.
      showVars(root, null);
    }
  }

  /** An unparseable pattern is ignored silently by the browser. Say so in the editor. */
  function warnAboutPatterns(root) {
    toArray(root.querySelectorAll('[pattern]')).forEach(function (control) {
      var pattern = control.getAttribute('pattern');
      try {
        new RegExp(pattern, 'v');
      } catch (error) {
        console.warn('[n8n-form] invalid pattern on "' + control.getAttribute('data-name') + '": ' + pattern, error);
        var notice = document.createElement('small');
        notice.textContent = 'Theme editor: this field’s validation pattern is not valid and is being ignored.';
        control.parentNode.appendChild(notice);
      }
    });
  }

  /**
   * Every field the answer carries, listed in the theme editor only.
   *
   * The template is written against whatever n8n returns, and nothing in the theme knows those
   * names before a row arrives. So the row IS the documentation: each key is printed as the
   * `[name]` the owner would type, a list as the block that repeats over it, with the value
   * beside it. Nothing of this renders for a backer.
   */
  function showVars(root, data) {
    if (!(window.Shopify && window.Shopify.designMode)) return;

    var panel = root.querySelector('[data-n8n-vars]');
    if (!panel) {
      panel = document.createElement('details');
      panel.className = 'n8n-form__vars';
      panel.setAttribute('data-n8n-vars', '');
      panel.open = true;
      var title = document.createElement('summary');
      title.textContent = 'Theme editor: the names this answer carries';
      panel.appendChild(title);
      root.appendChild(panel);
    }

    var list = document.createElement('dl');
    var names = data && typeof data === 'object' ? Object.keys(data) : [];
    // Nothing has answered yet. Said out loud, because a panel that is silently empty reads as a
    // form with no names at all.
    if (!names.length) {
      var note = document.createElement('dd');
      note.textContent = 'No answer yet. Put a backer’s k value in “Theme editor preview key” and'
        + ' this lists every name their row carries.';
      list.appendChild(note);
    }
    names.forEach(function (name) {
      var value = data[name];
      var items = Array.isArray(value);
      var term = document.createElement('dt');
      term.textContent = items ? '[#' + name + '] … [/' + name + ']' : '[' + name + ']';
      var what = document.createElement('dd');
      if (items) {
        var first = value[0];
        var inner = first && typeof first === 'object' ? Object.keys(first) : ['value'];
        what.textContent = value.length + ' items, each with ' + inner.map(function (key) {
          return '[' + key + ']';
        }).join(' ');
      } else if (value && typeof value === 'object') {
        what.textContent = Object.keys(value).map(function (key) {
          return '[' + name + '.' + key + ']';
        }).join(' ');
      } else {
        what.textContent = String(value);
      }
      list.appendChild(term);
      list.appendChild(what);
    });

    var old = panel.querySelector('dl');
    if (old) panel.removeChild(old);
    panel.appendChild(list);
  }

  function initAll(scope) {
    toArray((scope || document).querySelectorAll(ROOT_SELECTOR)).forEach(init);
    if (scope && scope.matches && scope.matches(ROOT_SELECTOR)) init(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  // Clicking a field in the editor sidebar must reveal the step holding it.
  document.addEventListener('shopify:block:select', function (event) {
    // A selected step is itself the container; a selected field is inside one.
    var target = event.target.closest ? event.target.closest('[data-n8n-step]') : null;
    if (!target) target = event.target.querySelector('[data-n8n-step]');
    if (!target) return;

    var root = target.closest(ROOT_SELECTOR);
    if (!root) return;

    // Go through show(), so the buttons and the progress line match the step
    // on screen. Toggling the step alone left Send showing on a middle step.
    var index = stepsIn(root).indexOf(target);
    if (index < 0 || !root._n8nShow) return;
    root._n8nShow(index);
  });
})();
